/**
 * The ways this application says "not yet".
 *
 * `Skeleton` for a region whose shape is known, `LoadingState` for one whose
 * shape is not, both drawn as daisyUI skeletons; `Spinner` only inside a
 * control that is working. A wait is never a line of "Loading…" text.
 *
 * Each announces through `role="status"`, unless the caller keeps its own
 * region across states and passes `silent`. A region that enters the DOM with
 * its text already in place is usually not announced, so a caller that renders
 * this only while loading should own the region rather than mount one here.
 *
 * @module components/ui/LoadingState
 */

import React from "react";

import { Loading } from "react-daisyui";

export interface SpinnerProps {
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

/** Bare spinner, for inside a control that already has its own label. */
export function Spinner({ size = "sm", className = "" }: SpinnerProps): React.ReactElement {
  return <Loading variant="spinner" size={size} className={className} aria-hidden="true" />;
}

export interface LoadingStateProps {
  /** Names what is loading: "Loading modules", not "Loading". Read, not drawn. */
  label: string;
  /** Fills the content area, for a route that has nothing to show yet. */
  fullPage?: boolean;
  /** Rows of placeholder text under the title bar. */
  lines?: number;
  /**
   * What the wait is shaped like: `lines` of text, a `table` of rows, a
   * `panel` with a title bar, or a whole `page` (heading, tab strip, panel).
   */
  shape?: "lines" | "table" | "panel" | "page";
  /**
   * Drops the live region, for a caller that keeps its own across states. A
   * region mounted with its text already inside it is generally not announced,
   * so two of them is one announcement at best and a duplicate at worst.
   */
  silent?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** The grey bars a wait is drawn as. Decorative: the label carries the meaning. */
function SkeletonBars({
  lines,
  fullPage,
}: {
  lines: number;
  fullPage: boolean;
}): React.ReactElement {
  return (
    <div aria-hidden="true" className="flex w-full flex-col gap-2">
      {fullPage ? <div className="skeleton mb-2 h-6 w-1/3" /> : null}
      {Array.from({ length: lines }, (_, index) => (
        <div
          // Placeholder rows have no identity beyond their position, and the
          // list never reorders, so the index is the only key available.
          key={`skeleton-${String(index)}`}
          className={`skeleton h-4 ${index === lines - 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  );
}

/** One placeholder bar. Position is its only identity. */
function Bar({ className }: { className: string }): React.ReactElement {
  return <div className={`skeleton ${className}`} />;
}

/** Placeholder rows of a table, under a header bar. */
function TableBars({ rows }: { rows: number }): React.ReactElement {
  return (
    <div aria-hidden="true" data-skeleton="skeleton-table" className="flex w-full flex-col gap-2">
      <Bar className="h-8 w-full" />
      {Array.from({ length: rows }, (_, index) => (
        <Bar key={`row-${String(index)}`} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** A bordered surface with a title bar over placeholder text. */
function PanelBars({ lines }: { lines: number }): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      data-skeleton="skeleton-panel"
      className="flex w-full flex-col gap-3 rounded-box border border-base-300 p-6 max-sm:p-4"
    >
      <Bar className="h-6 w-1/3" />
      <SkeletonBars lines={lines} fullPage={false} />
    </div>
  );
}

/** Heading, tab strip and a panel: the frame most screens share. */
function PageBars({ lines }: { lines: number }): React.ReactElement {
  return (
    <div aria-hidden="true" data-skeleton="skeleton-page" className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Bar className="h-7 w-1/4" />
        <Bar className="h-4 w-1/2" />
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Bar key={`tab-${String(index)}`} className="h-9 w-24" />
        ))}
      </div>
      <PanelBars lines={lines} />
    </div>
  );
}

/**
 * A wait, drawn as daisyUI skeleton bars and announced once by its label.
 *
 * For a region whose shape is not known; where it is, `Skeleton` or a
 * skeleton shaped like the content says more about what is coming.
 */
export function LoadingState({
  label,
  fullPage = false,
  lines = fullPage ? 5 : 3,
  shape = "lines",
  silent = false,
  className = "",
  "data-testid": testId,
}: LoadingStateProps): React.ReactElement {
  return (
    <div
      role={silent ? undefined : "status"}
      aria-live={silent ? undefined : "polite"}
      className={`${fullPage ? "min-h-[40vh]" : ""} ${className}`.trim() || undefined}
      data-testid={testId}
    >
      <span className="sr-only">{label}</span>
      {shape === "table" ? <TableBars rows={lines} /> : null}
      {shape === "panel" ? <PanelBars lines={lines} /> : null}
      {shape === "page" ? <PageBars lines={lines} /> : null}
      {shape === "lines" ? <SkeletonBars lines={lines} fullPage={fullPage} /> : null}
    </div>
  );
}

export interface SkeletonProps {
  /** Rows of placeholder text. */
  lines?: number;
  /** `block` for a card-shaped region, `text` for lines of copy, `control` for a checkbox-sized square, `field` for a textarea. */
  shape?: "text" | "block" | "control" | "field";
  /** Announced while the placeholder stands in for content. */
  label?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * A placeholder shaped like the content it waits for, so the layout does not
 * jump when the content lands.
 */
export function Skeleton({
  lines = 3,
  shape = "text",
  label = "Loading",
  className = "",
  "data-testid": testId,
}: SkeletonProps): React.ReactElement {
  if (shape === "control") {
    return (
      <span
        role="status"
        aria-label={label}
        className={`skeleton inline-block size-5 shrink-0 ${className}`.trim()}
        data-testid={testId}
      />
    );
  }
  if (shape === "field") {
    return (
      <div
        role="status"
        aria-label={label}
        className={`skeleton h-24 w-full ${className}`.trim()}
        data-testid={testId}
      />
    );
  }
  if (shape === "block") {
    return (
      <div
        role="status"
        aria-label={label}
        className={`skeleton h-32 w-full ${className}`.trim()}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      role="status"
      aria-label={label}
      className={`flex flex-col gap-2 ${className}`.trim()}
      data-testid={testId}
    >
      {Array.from({ length: lines }, (_, index) => (
        <div
          // Placeholder rows have no identity beyond their position, and the
          // list never reorders, so the index is the only key available.
          key={`skeleton-${String(index)}`}
          className={`skeleton h-4 ${index === lines - 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  );
}
